import React from "react";
import { motion } from "framer-motion";
import { Radio as RadioIcon, Info } from "lucide-react";
import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";
import { Label } from "@/components/ui/label";
import { BANDA_STYLES, BANDA_PERFIL } from "@/lib/constants";

const Chip = ({ active, onClick, children, testId }) => (
  <button type="button" data-testid={testId} onClick={onClick}
    className={`rounded-full border px-3.5 py-2 text-sm transition-colors ${active ? "border-[color:var(--brand-marsala)] bg-[color:var(--brand-marsala-soft)] text-[color:var(--brand-marsala)]" : "border-[color:var(--brand-border)] bg-white text-[color:var(--brand-graphite)] hover:bg-[color:var(--brand-paper)]"}`}>{children}</button>
);

export const BandaStep = ({ banda, onChange }) => {
  const b = banda || { estilos: [] };
  const set = (k, v) => onChange({ ...b, [k]: v });
  const toggle = (s) => set("estilos", (b.estilos || []).includes(s) ? b.estilos.filter((x) => x !== s) : [...(b.estilos || []), s]);
  return (
    <motion.div initial={{ opacity: 0, y: 8 }} animate={{ opacity: 1, y: 0 }} transition={{ duration: 0.35 }} className="mx-auto max-w-2xl">
      <h2 className="flex items-center gap-2 font-serif text-2xl font-semibold text-[color:var(--brand-graphite)] sm:text-3xl"><RadioIcon className="h-6 w-6 text-[color:var(--brand-marsala)]" /> Banda / Receptivo</h2>
      <p className="mt-2 text-sm text-[color:var(--brand-muted)]">Conte um pouco sobre a identidade musical que vocês imaginam para esse momento.</p>

      <div className="mt-6">
        <Label>O que foi contratado?</Label>
        <div className="mt-2 grid gap-2 sm:grid-cols-3">
          {[["banda", "Apenas Banda"], ["receptivo", "Apenas Receptivo"], ["ambos", "Os dois"]].map(([v, lbl]) => (
            <button key={v} type="button" data-testid={`banda-formato-${v}`} onClick={() => set("formato", v)}
              className={`rounded-xl border px-4 py-3 text-sm font-medium transition-colors ${b.formato === v ? "border-[color:var(--brand-marsala)] bg-[color:var(--brand-marsala-soft)] text-[color:var(--brand-marsala)]" : "border-[color:var(--brand-border)] bg-white text-[color:var(--brand-graphite)] hover:bg-[color:var(--brand-paper)]"}`}>
              {lbl}
            </button>
          ))}
        </div>
      </div>

      <div className="mt-6">
        <p className="mb-3 text-sm font-semibold text-[color:var(--brand-graphite)]">Quais estilos vocês gostariam de ouvir?</p>
        <div className="flex flex-wrap gap-2">
          {BANDA_STYLES.map((s) => <Chip key={s} active={(b.estilos || []).includes(s)} onClick={() => toggle(s)} testId={`banda-style-${s}`}>{s}</Chip>)}
        </div>
        {(b.estilos || []).includes("Outros") && (
          <div className="mt-3"><Label>Quais?</Label><Input data-testid="banda-outros" value={b.estilos_outros || ""} onChange={(e) => set("estilos_outros", e.target.value)} className="mt-1.5 h-11" /></div>
        )}
      </div>

      <div className="mt-6"><Label>Existe alguma música ou artista que vocês gostariam muito de ouvir?</Label>
        <Textarea data-testid="banda-desejadas" value={b.musicas_desejadas || ""} onChange={(e) => set("musicas_desejadas", e.target.value)} placeholder="Ex.: Bruno Mars, Coldplay, Djavan, Tim Maia, Maroon 5..." className="mt-1.5" /></div>

      <div className="mt-4"><Label>Existe alguma música, artista ou estilo que vocês NÃO gostariam de ouvir?</Label>
        <Textarea data-testid="banda-nao-desejadas" value={b.musicas_nao_desejadas || ""} onChange={(e) => set("musicas_nao_desejadas", e.target.value)} className="mt-1.5" /></div>

      <p className="mt-2 flex items-start gap-2 text-xs text-[color:var(--brand-muted)]"><Info className="mt-0.5 h-3.5 w-3.5 shrink-0" /> * Essas informações serão usadas como referência para a criação e o direcionamento do repertório.</p>

      <div className="mt-6">
        <p className="mb-3 text-sm font-semibold text-[color:var(--brand-graphite)]">Como vocês imaginam esse momento? (opcional)</p>
        <div className="flex flex-wrap gap-2">
          {BANDA_PERFIL.map((p) => <Chip key={p} active={b.perfil === p} onClick={() => set("perfil", b.perfil === p ? "" : p)} testId={`banda-perfil-${p}`}>{p}</Chip>)}
        </div>
        {b.perfil === "Outro" && (
          <div className="mt-3"><Input data-testid="banda-perfil-outro" value={b.perfil_outro || ""} onChange={(e) => set("perfil_outro", e.target.value)} placeholder="Conte como imaginam" className="h-11" /></div>
        )}
      </div>
    </motion.div>
  );
};
